import { useEffect, useMemo, useRef, useState } from 'react';
import { useScrollLock } from '../../components/useScrollLock.ts';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { api } from '../../lib/api.ts';
import { cx } from '../../components/ui.tsx';
import { BRAND, BrandIcon, FileTypeIcon } from '../../components/icons.tsx';
import { formatRelative } from '../../lib/display.ts';

/**
 * Search across the whole system, from the header.
 *
 * The user's question is "where is that SHAHEEN workbook", not "which folder
 * was it filed under", so one box searches folders, files and sheets at once.
 *
 * Keyboard-first: Ctrl+K opens it from anywhere, the arrows move through
 * results across group boundaries, and Enter opens the highlighted one.
 */

type SearchKind = 'folder' | 'file' | 'spreadsheet';

interface SearchHit {
  kind: SearchKind;
  id: string;
  title: string;
  subtitle?: string;
  href: string;
  updatedAt?: string;
}

interface SearchResults {
  term: string;
  total: number;
  groups: Array<{ kind: SearchKind; label: string; hits: SearchHit[] }>;
}

/** The same artwork the Drive uses, so a hit looks like the thing it is. */
function HitIcon({ hit }: { hit: SearchHit }) {
  if (hit.kind === 'folder') return <BrandIcon src={BRAND.folder} size={20} />;
  if (hit.kind === 'spreadsheet') return <BrandIcon src={BRAND.excel} size={20} />;
  return <FileTypeIcon name={hit.title} size={20} />;
}

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const [highlight, setHighlight] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Ctrl+K from anywhere. The browser's own find is Ctrl+F and stays free.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const debounced = useDebounced(term, 200);

  const query = useQuery({
    queryKey: ['search', debounced],
    queryFn: () => api.get<SearchResults>(`/api/search?q=${encodeURIComponent(debounced)}`),
    enabled: open && debounced.trim().length >= 2,
    staleTime: 15_000,
  });

  // Flattened, so the arrow keys walk across group boundaries the way the eye
  // does rather than stopping at each heading.
  const flat = useMemo(
    () => (query.data?.groups ?? []).flatMap((group) => group.hits),
    [query.data],
  );

  useEffect(() => setHighlight(0), [debounced]);

  // The page behind the search dialog holds still, as it does behind the
  // file viewer.
  useScrollLock(open);

  function go(hit: SearchHit) {
    setOpen(false);
    setTerm('');
    navigate(hit.href);
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        /*
          As wide as the header lets it be.
          
          It used to be a stated 45vw, which on a phone is 175px — narrower
          than the words inside it, so "files, sheets and folders…" ran out of
          the box and over the page behind. A search box is also the one
          control that is better for being big: it is what the whole header is
          for. So it takes the room that is going, up to a limit that keeps it
          from swallowing a wide desktop whole.
        */
        className="flex h-11 min-w-0 flex-1 items-center gap-2.5 rounded-lg border border-border-input bg-sunken px-3.5 text-left text-body-sm text-subtle transition-fast hover:border-[var(--blue-400)] hover:bg-surface md:max-w-[46rem]"
      >
        <SearchGlyph />
        <span className="flex-1 truncate">Search files, sheets and folders…</span>
        {/* A keyboard hint is for people with a keyboard, and it is the first
            thing worth dropping when the width is a phone's. */}
        <kbd className="hidden rounded border border-border bg-surface px-1.5 py-0.5 font-mono text-[11px] text-muted sm:block">
          Ctrl K
        </kbd>
      </button>

      {open
        ? createPortal(
            <div
              className="fixed inset-0 z-[88] flex items-start justify-center bg-[rgba(12,26,43,0.45)] p-4 pt-[10vh]"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) setOpen(false);
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label="Search"
                className="w-full max-w-2xl overflow-hidden rounded-xl border border-border-strong bg-surface shadow-2xl"
              >
                <div className="flex items-center gap-3 border-b border-border px-4">
                  <span className="text-muted">
                    <SearchGlyph />
                  </span>
                  <input
                    ref={inputRef}
                    value={term}
                    onChange={(event) => setTerm(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        setOpen(false);
                      } else if (event.key === 'ArrowDown') {
                        event.preventDefault();
                        setHighlight((current) => (flat.length ? (current + 1) % flat.length : 0));
                      } else if (event.key === 'ArrowUp') {
                        event.preventDefault();
                        setHighlight((current) =>
                          flat.length ? (current - 1 + flat.length) % flat.length : 0,
                        );
                      } else if (event.key === 'Enter') {
                        event.preventDefault();
                        const hit = flat[highlight];
                        if (hit) go(hit);
                      }
                    }}
                    placeholder="Search sheets, files and folders…"
                    aria-label="Search everything"
                    className="h-14 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-subtle"
                  />
                  {query.isFetching ? (
                    <span className="text-micro text-muted">Searching…</span>
                  ) : null}
                </div>

                <div className="max-h-[55vh] overflow-y-auto">
                  {debounced.trim().length < 2 ? (
                    <p className="px-4 py-8 text-center text-body-sm text-muted">
                      Type at least two characters.
                    </p>
                  ) : query.data && query.data.total === 0 ? (
                    <p className="px-4 py-8 text-center text-body-sm text-muted">
                      Nothing matches “{debounced}”.
                    </p>
                  ) : (
                    (query.data?.groups ?? []).map((group) => (
                      <section key={group.kind}>
                        <h3 className="sticky top-0 bg-sunken px-4 py-1 text-micro font-semibold uppercase tracking-wide text-muted">
                          {group.label}
                        </h3>
                        {group.hits.map((hit) => {
                          const index = flat.indexOf(hit);
                          return (
                            <button
                              key={`${hit.kind}-${hit.id}`}
                              type="button"
                              onMouseEnter={() => setHighlight(index)}
                              onClick={() => go(hit)}
                              className={cx(
                                'flex w-full items-center gap-3 px-4 py-2 text-left transition-fast',
                                index === highlight ? 'bg-[var(--blue-50)]' : 'hover:bg-sunken',
                              )}
                            >
                              <span className="shrink-0" aria-hidden>
                                <HitIcon hit={hit} />
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="block truncate text-body-sm text-ink">
                                  {hit.title}
                                </span>
                                {hit.subtitle ? (
                                  <span className="block truncate text-micro text-muted">
                                    {hit.subtitle}
                                  </span>
                                ) : null}
                              </span>
                              {hit.updatedAt ? (
                                <span className="shrink-0 text-micro text-subtle">
                                  {formatRelative(hit.updatedAt)}
                                </span>
                              ) : null}
                            </button>
                          );
                        })}
                      </section>
                    ))
                  )}
                </div>

                <div className="flex items-center gap-4 border-t border-border bg-sunken px-4 py-1.5 text-[11px] text-muted">
                  <span>↑↓ to move</span>
                  <span>Enter to open</span>
                  <span>Esc to close</span>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

/** Waits for typing to pause, so one query goes out instead of one per key. */
function useDebounced(value: string, delay: number): string {
  const [settled, setSettled] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return settled;
}

function SearchGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="7" cy="7" r="4.5" />
      <path d="M10.5 10.5 14 14" strokeLinecap="round" />
    </svg>
  );
}
